/* eslint-disable max-len */
import React from 'react';
import { Link, Typography } from '@material-ui/core';
import {
  Star, Savings, Online, Checkmark, InfoFilled
} from '@one-thd/sui-icons';
import { track } from '../analytics/analytics';
import { FAQ_LINKS } from './analytics';
import { getCheckoutAccessTypeLabel } from './general';

export const UNVERIFIED_ADMIN = 'unverified-admin';
export const UNVERIFIED_PURCHASER = 'unverified-purchaser';
export const UNVERIFIED_DIY = 'unverified-DIY';
export const VERIFIED_ADMIN = 'verified-admin';
export const VERIFIED_PURCHASER = 'verified-purchaser';
export const VERIFIED_USER = 'verified-user';

export const ROUTES = {
  home: {
    label: 'Home',
    url: '/',
  },
  landing: {
    label: 'Military Discount Benefit',
    url: '/military/discount',
  },
  verification: {
    label: 'Verification',
    url: '/military/discount',
  },
  b2bAccount: {
    label: 'Account',
    url: '/b2b/account/view/landingpage',
  },
  b2cAccount: {
    label: 'Account',
    url: '/myaccount/profile',
  },
  b2cAccountMenu: {
    label: 'Account',
    url: '/myaccount/profile/menu',
  },
  sheerId: {
    label: 'SheerID',
    url: 'https://offers.sheerid.com/sheerid/help-center/?pid=6179ae2959e1633ef316ed52',
  },
  shopMyExchange: {
    label: 'ShopMyExchange.com',
    url: 'https://www.shopmyexchange.com/a/3539981?iid=home-depot',
  },
  myNavyExchange: {
    label: 'MyNavyExchange.com',
    url: 'https://www.mynavyexchange.com/homedepot',
  },
};

const whoCanQualifyContent = {
  title: 'Who Can Qualify',
  FAQs: [
    {
      question: 'Who is eligible for the Military Discount?',
      answer: (
        <>
          <Typography variant="body-base">
            {' '}
            Active and reservist members, veterans and retirees of the following branches of the
            United States military:
          </Typography>
          <ul className="sui-pl-4 sui-mb-4">
            <li>&mdash; Army</li>
            <li>&mdash; Navy</li>
            <li>&mdash; Air Force</li>
            <li>&mdash; Marine Corps</li>
            <li>&mdash; Coast Guard</li>
            <li>&mdash; Space Force</li>
            <li>&mdash; National Oceanic and Atmospheric Administration (NOAA)</li>
            <li>&mdash; US Public Health Service Commissioned Corps (USPHS)</li>
            <li>&mdash; National Guard</li>
          </ul>
          <Typography variant="body-base">
            The Military Discount is also available to the current spouse of an eligible service
            member and Gold Star spouses.
          </Typography>
        </>
      ),
      analyticsValue: 'who is eligible'
    },
    {
      question: 'Who is not eligible for the Military Discount?',
      answer:
        'The following are not eligible to receive the Military Discount: non-spousal dependents of active military and veterans; dishonorably discharged veterans; non-military members; auxiliary members; and civilian employees of the military.',
      analyticsValue: 'who is not eligible'
    },
    {
      question: 'Are military spouses eligible to use the Military Discount?',
      answer: (
        <Typography variant="body-base">
          Yes. Spouses of eligible service members (includes Gold Star spouses) must verify with
          their first name, last name, account email with The Home Depot and the service
          member&apos;s required information. Visit{' '}
          <Link underline="always" color="text-colors-primary" href="#top" onClick={() => track(FAQ_LINKS, { value: 'Military Discount Benefit' })}>
            Military Discount Benefit
          </Link>
          , select &ldquo;Get Started&rdquo; and follow the on-screen instructions to enroll.
        </Typography>
      ),
      analyticsValue: 'spouse eligibility'
    },
  ],
};

const howToEnrollContent = {
  title: 'How To Enroll',
  FAQs: [
    {
      question: 'How can I enroll in the Military Discount?',
      answer: (
        <Typography variant="body-base">
          To enroll, visit{' '}
          <Link
            href="#top"
            underline="always"
            color="text-colors-primary"
            onClick={() => track(FAQ_LINKS, { value: 'Military Discount Benefit' })}
          >
            Military Discount Benefit
          </Link>{' '}
          or open The Home Depot mobile app. Log in to your existing personal account or create a
          new account. Go to the Military Discount Benefit page, select &ldquo;Get Started&rdquo;
          and follow the on-screen instructions to complete your military status verification. Once
          you complete all steps, your personal account on HomeDepot.com will reflect your enrolled
          Military Discount status.
        </Typography>
      ),
      analyticsValue: 'how can i enroll',
    },
    {
      question: 'How does The Home Depot verify proof of military service?',
      answer: (
        <>
          <Typography variant="body-base">
            The Home Depot partners with SheerID, our trusted third-party verification service,
            to use private and secure data sources to confirm the accuracy of the information you provide.
            The verification process typically takes a few minutes. In an effort to assist our customers comply
            with law, SheerID cannot accept Military IDs from currently serving Military personnel or employees
            of the Federal Government to verify military status.  If prompted for documents, please upload a
            different document to verify your military status.
          </Typography>
          <div className="sui-mt-4">
            <Typography variant="body-base">
              You only need to verify your military status once. Once your account is set up, you may
              access the Military Discount for all eligible purchases.
            </Typography>
          </div>
        </>),
      analyticsValue: 'verify proof of service',
    },
    {
      question: 'What do I do if I have issues enrolling?',
      answer: (
        <Typography variant="body-base">
          Visit{' '}
          <Link
            underline="always"
            color="text-colors-primary"
            target="_blank"
            href={ROUTES.sheerId.url}
            onClick={() => track(FAQ_LINKS, { value: ROUTES.sheerId.label })}
          >
            {ROUTES.sheerId.label}
          </Link>{' '}
          for assistance with the registration process and/or to get additional information on the
          documents required for verification.
        </Typography>
      ),
      analyticsValue: 'issues enrolling',
    },
  ],
};

const getUsingMilitaryDiscountContent = (ENABLE_HD_PASS_VERBIAGE) => {
  const checkoutAccessType = getCheckoutAccessTypeLabel(ENABLE_HD_PASS_VERBIAGE);

  return ({
    title: 'Using the Military Discount',
    FAQs: [
      {
        question: 'How do I use my Military Discount?',
        answer: (
          <>
            <Typography variant="body-base">
              To use it online, log in to your account on HomeDepot.com. You&apos;ll see the Military
              Discount automatically applied to eligible items in cart during checkout.
            </Typography>
            <div className="sui-mt-4">
              <Typography variant="body-base">
                To use in store, go to the checkout register. Scan your {checkoutAccessType} from The Home Depot
                App or enter the phone number you registered with The Home Depot account. Your
                Military Discount will be applied to eligible items during checkout.
              </Typography>
            </div>
          </>
        ),
        analyticsValue: 'how do i use',
      },
      {
        question: 'How much can I save with the Military Discount?',
        answer: (
          <Typography variant="body-base">
            The Military Discount applies 10% off the price of select
            in-stock purchases made by eligible participants. Eligible participants receive a{' '}
            $400 maximum annual discount that resets each calendar
            year.
          </Typography>
        ),
        analyticsValue: 'how much can i save',
      },
      {
        question: 'What items are excluded from the Military Discount?',
        answer:
          'The Military Discount can\'t be applied to select items, services and fees. It includes, but isn\'t limited to, some commodity products (including lumber, wire, building materials), value priced merchandise, appliances, special order items, tool rental fees, labor items, gift cards and services including freight and delivery.',
        analyticsValue: 'what items excluded',
      },
      {
        question: 'Can I purchase appliances using the Military Discount?',
        answer: (
          <>
            <Typography variant="body-base">
              Most in-store and online appliances are excluded from our discount benefit.
            </Typography>
            <div className="sui-mt-4">
              <Typography variant="body-base">
                Active-duty service members, dependents, retirees, Department of Defense civilians and
                retirees, and honorably discharged veterans may purchase Home Depot appliances through
                a partnership with Military Exchanges.
              </Typography>
            </div>
            <div className="sui-mt-4">
              <Typography variant="body-base">
                Shoppers enjoy special military pricing and tax-free purchases.
              </Typography>
            </div>
            <div className="sui-mt-4">
              <Typography variant="body-base">
                Visit{' '}
                <Link
                  underline="always"
                  color="text-colors-primary"
                  target="_blank"
                  href={ROUTES.shopMyExchange.url}
                  onClick={() => track(FAQ_LINKS, { value: ROUTES.shopMyExchange.label })}
                >
                  {ROUTES.shopMyExchange.label}
                </Link>{' '}
                or{' '}
                <Link
                  underline="always"
                  color="text-colors-primary"
                  target="_blank"
                  href={ROUTES.myNavyExchange.url}
                  onClick={() => track(FAQ_LINKS, { value: 'MyNavyExchange.com' })}
                >
                  MyNavyExchange.com
                </Link>{' '}
                to learn more.
              </Typography>{' '}
            </div>
          </>
        ),
        analyticsValue: 'can i purchase appliance',
      },
    ],
  });
};

const accountMaintenanceContent = {
  title: 'Account Maintenance',
  FAQs: [
    {
      question:
        'Can a spouse and military service member share one account with The Home Depot and receive the Military Discount?',
      answer: (
        <>
          <Typography variant="body-base">
            Eligible service members and eligible spouses must have separate personal accounts on
            HomeDepot.com and enroll in the Military Discount separately.
          </Typography>
          <div className="sui-mt-4">
            <Typography variant="body-base">
              Eligible spouses can register for their own account with The Home Depot at{' '}
              <Link underline="always" color="text-colors-primary" href="#top" onClick={() => track(FAQ_LINKS, { value: 'Military Discount Benefit' })}>
                Military Discount Benefit
              </Link>
              .
            </Typography>
          </div>
        </>
      ),
      analyticsValue: 'spouse share one account'
    },
  ],
};

export const getUnenrolledGroupedFAQs = (ENABLE_HD_PASS_VERBIAGE) => ([
  whoCanQualifyContent,
  howToEnrollContent,
  getUsingMilitaryDiscountContent(ENABLE_HD_PASS_VERBIAGE),
  accountMaintenanceContent,
]);

export const getEnrolledGroupedFAQs = (ENABLE_HD_PASS_VERBIAGE) => ([
  getUsingMilitaryDiscountContent(ENABLE_HD_PASS_VERBIAGE),
  accountMaintenanceContent,
  whoCanQualifyContent,
  howToEnrollContent,
]);

export const faqLegalCopy = (ENABLE_TC_CHANGES) => (
  <>
    <span>*Eligible participants receive a $400 maximum annual discount which resets each calendar year. </span>
    {ENABLE_TC_CHANGES ? (
      <span> The discount cannot be combined with other promotions, or applied to clearance items or prior purchases, and is for personal use only. </span>
    ) : (
      <span> The discount cannot be combined with other promotions or previous purchases and is for personal use only. </span>
    )}
    <span>
      The Home Depot reserves the right to change eligibility requirements or terminate the
      discount program at any time, with or without prior notice to eligible participants. Any changes
      will be effective immediately, and participants waive any right to receive specific notice of
      such changes. The Home Depot reserves the right to revoke enrollment status based on alleged,
      suspected and/or actual evidence of misuse or fraud in our sole and absolute discretion. Offer
      is not redeemable for cash, is non-transferable and cannot be combined with manager-discretion
      price adjustments. Additional exclusions may apply. See store for details.
    </span>
  </>
);

export const onlyAdminEnrollText = 'Only Pro account admins can enroll in the Military Discount.';
export const maxDiscountText = 'You’ve reached your annual savings limit. The discount limit will reset on January 1.';

export const unverifiedAdminCheckList = [
  {
    title: onlyAdminEnrollText,
    icon: <Checkmark size="small" color="brand" />,
  },
  {
    title: 'Only one Military Discount can be applied per Pro account.',
    icon: <Checkmark size="small" color="brand" />,
  },
  {
    title: 'Discount applies to purchases made by any user on the account.',
    icon: <Checkmark size="small" color="brand" />,
  },
];

export const unverifiedPurchaserCheckList = [
  {
    title: onlyAdminEnrollText,
    icon: <InfoFilled size="small" color="info" />,
  },
];

export const unverifiedBenefitsList = [
  {
    title: 'Verify Military Status',
    description:
      'You may be required to provide proof of status with a government-issued document.',
    icon: <Star />,
  },
  {
    title: 'Check Your Email',
    description: 'Receive an email when your status is confirmed.',
    icon: <Online />,
  },
  {
    title: 'Enjoy Your 10% Savings',
    description: 'Shop online or in store to receive your discount on eligible purchases.',
    icon: <Savings />,
  },
];

export const getVerifiedBenefitsList = (checkoutAccessType) => ([
  {
    title: 'Shop Online',
    description: 'See your discount automatically applied in cart.',
  },
  {
    title: 'Shop in Store',
    description:
      `Apply your discount at the checkout register. Scan your ${checkoutAccessType} from The Home Depot App or enter your phone number.`,
  },
]);
